<template>
  <div>
    <proReportSection :getProFilterListSlotRef="getProFilterListRef">
      <template slot="customDataRow">
        <el-tabs v-model="activeTab" @tab-click="handleTabClick">
          <el-tab-pane label="Source Records" name="sourceRecords">
            <div element-loading-spinner="atom-audit-loader">
              <proListFilter ref="proFilterList"></proListFilter>
            </div>
          </el-tab-pane>
          <el-tab-pane label="Sampling & Import" name="previewSample" lazy>
            <div class="importSettings">
              <el-row :gutter="20" type="flex" align="middle">
                <el-col :span="12">
                  <h4>Import Settings</h4>
                </el-col>
              </el-row>
              <el-row :gutter="20" style="margin-top: 5px;">
                <el-col :span="8">
                  <el-form size="mini" label-position="left" label-width="250px">
                    <el-form-item label="Sample Size Per Auditor">
                      <el-input v-model.number="sampleSizePerAuditor" size="mini" style="width: 150px;" placeholder="e.g. 10" @input="sampleSizePerAuditor = normalizeNumericInput($event, 1, 1000, sampleSizePerAuditor)"></el-input>
                    </el-form-item>
                    <el-form-item label="Target Code Type">
                      <hia-el-select v-model="targetCodeType" placeholder="Target Code Type" itemkey="key" label="value" :dropdowns="dropdowns.codeTypes.list" filterable style="width: 150px;"></hia-el-select>
                    </el-form-item>
                    <el-form-item label="Skip Previously Sampled Records">
                      <el-checkbox v-model="skipPreviouslySampledRecords" style="margin-top: 5px;"></el-checkbox>
                    </el-form-item>
                    <el-button style="margin-top: 5px; margin-left: 10px;" :disabled="loading || sampleSizePerAuditor <= 0" size="small" round @click="generatePreview()"> <i class="el-icon-view"></i> Update Sample </el-button>
                  </el-form>
                </el-col>
              </el-row>
            </div>

            <el-row class="dataRow" v-loading="previewLoading" element-loading-spinner="atom-audit-loader" style="margin-top: 20px;">
              <el-col :span="24">
                <div class="minimal-card previewCard">
                  <div class="previewHeader">
                    <h4>Sampled Auditors</h4>
                  </div>

                  <v-client-table class="hiaTable" ref="previewTable" :data="previewData" :columns="previewTableColumns" :options="previewTableOptions">
                    <template slot="auditorName" slot-scope="props">
                      <span @click="drilldown(props)" style="color: #337ab7; cursor: pointer;">
                        <i class="mdi" :class="props.row.selected ? 'mdi-minus-box-outline' : 'mdi-plus-box-outline'" style="font-size: 15px; position: relative; top: 1px;"></i>
                        {{ props.row.auditorName }}
                      </span>
                    </template>
                    <template slot="child_row" slot-scope="props">
                      <div class="child-table-wrapper">
                        <v-client-table class="hiaTable" :data="props.row.records || []" :columns="childTableColumns" :options="childTableOptions">
                          <template slot="reviewDate" slot-scope="child">
                            {{ formatDateCell(child.row.reviewDate) }}
                          </template>
                          <template slot="dateOfService" slot-scope="child">
                            {{ formatDateCell(child.row.dateOfService) }}
                          </template>
                        </v-client-table>
                      </div>
                    </template>
                  </v-client-table>

                  <div class="previewFooter">
                    <div class="previewTotal">
                      Total records to import:
                      {{ previewData.reduce((sum, a) => sum + (a.sampleCount || 0), 0) }}
                    </div>
                    <div class="previewActions">
                      <el-button v-show="showPreview" round :type="previewData.length > 0 ? 'primary' : ''" :disabled="previewData.length === 0 || importLoading" @click="proceedWithImport" size="small"> <i class="el-icon-upload2"></i> Import Records </el-button>
                    </div>
                  </div>
                </div>
              </el-col>
            </el-row>
          </el-tab-pane>
        </el-tabs>
      </template>
    </proReportSection>
  </div>
</template>

<script>
  import { mapState, mapActions, mapMutations } from 'vuex'
  import HiaAuthorization from '@/mixins/hiaAuthorization'
  import dateHelper from '@/mixins/date-helper'
  import inputValidations from '@/mixins/inputValidations'
  import proListFilter from '@/views/reports/proListFilter'
  import proReportSection from '@/views/reports/proReportSection'

  export default {
    name: 'Level2ImportPro',
    mixins: [HiaAuthorization, dateHelper, inputValidations],
    props: {
      projectId: {
        type: Number,
        required: true
      }
    },
    data() {
      return {
        loading: false,
        previewLoading: false,
        importLoading: false,
        showPreview: false,
        sampleSizePerAuditor: 10,
        skipPreviouslySampledRecords: true,
        targetCodeType: null,
        previewData: [],
        previewTableColumns: ['auditorName', 'totalRecords', 'sampleCount'],
        previewTableOptions: {
          showChildRowToggler: false,
          uniqueKey: 'auditorName',
          filterByColumn: true,
          headings: {
            auditorName: 'Auditor',
            totalRecords: 'Eligible',
            sampleCount: 'Sampled'
          },
          perPage: 20,
          highlightMatches: true,
          sortIcon: { is: 'none' }
        },
        childTableColumns: ['reviewDate', 'patientNumber', 'mrNumber', 'dateOfService', 'coder', 'physicianName', 'serviceLineName'],
        childTableOptions: {
          filterable: false,
          filterByColumn: false,
          headings: {
            reviewDate: 'Review Date',
            patientNumber: 'Patient Number',
            mrNumber: 'MR Number',
            dateOfService: 'DOS',
            coder: 'Coder',
            physicianName: 'Physician',
            serviceLineName: 'Service Line'
          },
          perPage: 10,
          highlightMatches: true,
          sortIcon: { is: 'none' }
        },
        activeTab: 'sourceRecords',
        isInitialized: false
      }
    },
    methods: {
      ...mapActions('proReviews/', ['RUN_PRO_COPYTOLEVEL2']),
      ...mapActions('reports/', ['GET_LEVEL2_IMPORT_PRO_PREVIEW', 'LOAD_PROREPORTDATA_FILTERED']),
      ...mapActions('reports/reportQueries/', ['GET_REPORTQUERIES', 'GET_REPORTQUERIES_USERS_LIST']),
      ...mapMutations('reports/', ['SET_REPORTS_DEFAULTS', 'SET_REPORTS_DEFAULTDATERANGE']),
      getProFilterListRef() {
        return this.$refs.proFilterList
      },
      async initialize() {
        this.SET_REPORTS_DEFAULTS({ enableLevel2Audits: true })

        const end = new Date()
        const start = new Date(new Date().getFullYear(), 0, 1)
        const defaultDateRange = [this.formatDate(start), this.formatDate(end)]
        this.SET_REPORTS_DEFAULTDATERANGE(defaultDateRange)

        this.loading = true
        await this.GET_REPORTQUERIES_USERS_LIST()
        await this.GET_REPORTQUERIES()

        try {
          const initialReport = {
            proData: {
              filters: this.proReport?.proData?.filters || [],
              vueTableData: {
                page: 1,
                limit: 1,
                orderBy: null,
                ascending: true
              }
            },
            globalSearch: null
          }

          await this.LOAD_PROREPORTDATA_FILTERED({ proReport: initialReport })

          // Initialize target code type from project default
          if (!this.targetCodeType) {
            this.targetCodeType = this.project?.defaultCodeType
          }

          this.loading = false
          this.isInitialized = true
        } catch (err) {
          this.loading = false
          this.$message({
            message: err.message || 'Error loading filters',
            type: 'error'
          })
        }
      },
      drilldown(props) {
        this.$refs.previewTable.toggleChildRow(props.row.auditorName)
      },
      async generatePreview() {
        if (this.sampleSizePerAuditor <= 0) {
          return
        }

        this.activeTab = 'previewSample'

        this.previewLoading = true
        try {
          const filters = this.proReport?.proData?.filters || []
          const globalSearch = this.proReport?.globalSearch || null

          const payload = {
            Filters: filters.filter(f => f.active),
            GlobalSearch: globalSearch,
            SampleSizePerAuditor: this.sampleSizePerAuditor,
            SkipPreviouslySampledRecords: this.skipPreviouslySampledRecords
          }

          const response = await this.GET_LEVEL2_IMPORT_PRO_PREVIEW(payload)

          this.previewData = response || []

          if (this.previewData.length === 0) {
            this.$message({
              message: 'No records found matching the filter criteria',
              type: 'info'
            })
            this.showPreview = false
          } else {
            this.showPreview = true
          }
        } catch (err) {
          const errorMessage = err.message || 'Error generating preview'
          this.$message({
            message: errorMessage,
            type: 'error'
          })
        } finally {
          this.previewLoading = false
        }
      },
      clearPreview() {
        this.previewData = []
        this.showPreview = false
      },
      formatDateCell(value) {
        return this.formatDate(value)
      },
      async proceedWithImport() {
        if (this.previewData.length === 0) {
          return
        }

        const allRecordIds = []
        this.previewData.forEach(auditor => {
          if (auditor.recordIds && Array.isArray(auditor.recordIds)) {
            allRecordIds.push(...auditor.recordIds)
          }
        })

        if (allRecordIds.length === 0) {
          this.$message({
            message: 'No records to import',
            type: 'warning'
          })
          return
        }

        try {
          await this.$confirm(`This will import ${allRecordIds.length} records into this project. Continue?`, 'Confirm Import', {
            confirmButtonText: 'Yes, Import',
            cancelButtonText: 'Cancel',
            type: 'warning'
          })
        } catch {
          return
        }

        this.importLoading = true
        try {
          const payload = {
            records: allRecordIds,
            targetProjectId: this.projectId,
            targetCodeType: this.targetCodeType || this.project?.defaultCodeType
          }

          await this.RUN_PRO_COPYTOLEVEL2(payload)

          this.$message({
            message: `Successfully imported ${allRecordIds.length} records`,
            type: 'success'
          })

          if (this.refreshProList) {
            this.refreshProList()
          }

          this.clearPreview()
          this.importLoading = false
        } catch (err) {
          this.importLoading = false
          const errorMessage = err.message || 'Error importing records'
          this.$message({
            message: errorMessage,
            type: 'error'
          })
        }
      },
      handleTabClick(tab) {
        if (tab.name === 'previewSample') {
          this.generatePreview()
        }
      }
    },
    computed: {
      ...mapState('reports/', ['proReport']),
      ...mapState('managedLists/projects/', ['project']),
      ...mapState('proReviews/', ['refreshProList']),
      ...mapState('dropdowns/', ['dropdowns'])
    },
    components: {
      proListFilter,
      proReportSection
    },
    watch: {
      'proReport.proData.filters': {
        handler(newFilters) {
          if (this.isInitialized && this.activeTab === 'previewSample' && newFilters) {
            this.generatePreview()
          }
        },
        deep: true
      },
      'proReport.globalSearch': {
        handler(newGlobalSearch) {
          if (this.isInitialized && this.activeTab === 'previewSample' && newGlobalSearch !== undefined) {
            this.generatePreview()
          }
        }
      }
    },
    created: function() {
      this.initialize()
    }
  }
</script>
<style scoped>
  .importSettings {
    border-top: 1px solid #eee;
    border-bottom: 1px solid #eee;
    margin-bottom: 10px;
    padding-bottom: 10px;
    display: flex;
    flex-direction: column;
    position: relative;
    min-height: 80px;
    /* background-color: #fafafa;
    padding: 0px 10px; */
  }

  .previewCard {
    margin-top: 10px;
  }

  .previewHeader {
    margin-bottom: 10px;
  }

  .previewFooter {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 10px;
    font-size: 12px;
  }

  .previewTotal {
    font-weight: 600;
  }

  .filterRow {
    margin: 10px 0px 0px 0px;
    min-height: 25px;
  }

  .dataRow {
    margin: 10px 0px 0px 0px;
  }

  .child-table-wrapper {
    margin-top: 10px;
    border-top: 1px solid #ebeef5;
    padding-top: 10px;
  }

  >>> .tagSpan {
    font-size: 10px;
    margin-left: 3px;
  }

  >>> .tagIcon {
    padding: 0 3px 0 0;
    margin: 2px 0 0 0;
    float: left;
  }

  .queryRow {
    margin: 0px 0px 10px 0px;
  }

  .filterRow {
    margin: 5px 0px 0px 0px;
    min-height: 25px;
  }

  .divider div {
    font-size: 12px;
  }

  .dataTabs {
    margin: 0px 0px 0px 0px;
  }

  .filterBlock {
    font-size: 11px;
    float: left;
    margin: 5px 0px 0px 3px;
  }

  .filterBlock button {
    padding: 3px 15px;
    width: 105px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .filterBlock button.removeFilter {
    width: auto;
  }

  .filterBlock .removeFilter {
    padding: 3px 5px;
  }

  .saveQuery {
    float: left;
    margin: 0px 0px 0px 5px;
  }

  .saveQuery button {
    padding: 3px 15px;
  }

  .saveQuery button span {
    font-size: 11px;
  }

  .hiaRangePicker {
    width: 300px;
  }

  .activeFilter {
    background: #ecf5ff;
  }

  .filterMenu.el-menu--collapse {
    width: 50px;
  }

  .filterMenu .el-menu-item,
  .filterMenu >>> .el-submenu__title {
    padding: 0px 0px 0px 0px;
    padding-left: 0px !important;
  }

  .filterMenu >>> .el-menu--inline .el-menu-item {
    padding-left: 10px !important;
  }

  >>> .filterActionButtons {
  }

  >>> .filterActionButtons button i {
    font-size: 12px;
  }

  >>> .filterActionButtons .el-popover__reference-wrapper {
    line-height: 0px;
    float: left;
    position: relative;
  }

  .queryTree >>> .el-tree-node .el-tree-node__content:hover {
    background-color: transparent;
  }

  .queryTree >>> .el-tree-node__children .el-tree-node__content:hover {
    background-color: #f5f7fa !important;
    border-radius: 20px;
  }

  .queryTree >>> .el-tree-node__content {
    height: unset;
  }

  .queryTree >>> .el-tree-node.is-current > .el-tree-node__content {
    background-color: transparent;
  }

  .queryTree >>> .el-tree-node__children > .is-current > .el-tree-node__content {
    background-color: #f5f7fa !important;
    border-radius: 20px;
  }

  /* beta styles */
  >>> .reportCard .el-card__body {
    padding: 5px;
  }

  >>> .queryCommandButton {
    width: 33%;
  }

  >>> .queryCommandButton .el-button--mini {
    width: 70%;
  }

  >>> .queryCommandButton .el-dropdown__caret-button {
    width: 30%;
  }

  >>> .queryActions {
    float: right;
  }

  >>> .queryActions button {
    padding: 7px 7px 0px 7px;
  }

  >>> .queryActions button span {
    font-size: 18px;
  }

  .reportDivider {
    margin: 14px 0;
  }

  .reportDivider .el-divider__text {
    font-size: 11px;
    padding: 0 10px;
    font-weight: 400;
  }

  .dataRow {
    margin: 10px 0px 0px 0px;
  }

  >>> .reportTabs .el-tabs__item {
    font-size: 18px;
  }

  >>> .dataTabs .el-tabs__item {
    font-size: 14px;
  }

  >>> .numberTag {
    background-color: rgba(255, 199, 46, 0.2);
    border-color: rgba(255, 199, 46, 0.2);
    color: rgba(255, 199, 46, 1);
  }

  >>> .textTag {
    background-color: rgba(0, 86, 149, 0.2);
    border-color: rgba(0, 86, 149, 0.2);
    color: #1b3c5f;
  }

  >>> .dateTag {
    background-color: rgba(124, 190, 95, 0.2);
    border-color: rgba(124, 190, 95, 0.2);
    color: #1b3c5f;
  }

  >>> .tagTag {
    background-color: rgba(249, 0, 137, 0.2);
    border-color: rgba(249, 0, 137, 0.2);
    color: #1b3c5f;
  }

  >>> .tagIcon {
    padding: 0px 3px 0px 0px;
    margin: 2px 0px 0px 0px;
    float: left;
  }

  >>> .greenIcon {
    color: rgba(124, 190, 95, 1);
  }

  >>> .magentaIcon {
    color: rgba(249, 0, 137, 1);
  }

  >>> .goldIcon {
    color: rgba(87, 204, 244, 1);
  }

  >>> .blueIcon {
    color: rgba(0, 86, 149, 1);
  }

  >>> .tagSpan {
    font-size: 11px;
    line-height: 11px;
  }

  >>> .filterIcon {
    float: left;
    font-size: 22px;
    padding-right: 10px;
  }

  >>> .filter_text .el-button {
    background: rgba(249, 0, 137, 0.6);
    color: #ffffff;
  }

  >>> .filter_text .el-button--primary:first-child {
    border-right: 0px solid rgba(249, 0, 137, 1);
    border-left: 1px solid rgba(249, 0, 137, 1);
    border-top: 1px solid rgba(249, 0, 137, 1);
    border-bottom: 1px solid rgba(249, 0, 137, 1);
  }

  >>> .filter_text .el-button--primary:last-child {
    border-right: 1px solid rgba(249, 0, 137, 1);
    border-left: 0px solid rgba(249, 0, 137, 1);
    border-top: 1px solid rgba(249, 0, 137, 1);
    border-bottom: 1px solid rgba(249, 0, 137, 1);
    margin-left: 1px;
  }

  >>> .filter_text .el-button--primary:last-child:hover {
    border-right: 1px solid #005695;
    border-top: 1px solid #005695;
    border-bottom: 1px solid #005695;
  }

  >>> .filter_text .el-button--primary:first-child:hover {
    border-left: 1px solid #005695;
    border-top: 1px solid #005695;
    border-bottom: 1px solid #005695;
  }

  >>> .filter_date .el-button {
    background: rgba(124, 190, 95, 0.7);
    color: #ffffff;
  }

  >>> .filter_date .el-button--primary:first-child {
    border-right: 0px solid rgba(124, 190, 95, 1);
    border-left: 1px solid rgba(124, 190, 95, 1);
    border-top: 1px solid rgba(124, 190, 95, 1);
    border-bottom: 1px solid rgba(124, 190, 95, 1);
  }

  >>> .filter_date .el-button--primary:last-child {
    border-right: 1px solid rgba(124, 190, 95, 1);
    border-left: 0px solid rgba(124, 190, 95, 1);
    border-top: 1px solid rgba(124, 190, 95, 1);
    border-bottom: 1px solid rgba(124, 190, 95, 1);
    margin-left: 1px;
  }

  >>> .filter_date .el-button--primary:last-child:hover {
    border-right: 1px solid #005695;
    border-top: 1px solid #005695;
    border-bottom: 1px solid #005695;
  }

  >>> .filter_date .el-button--primary:first-child:hover {
    border-left: 1px solid #005695;
    border-top: 1px solid #005695;
    border-bottom: 1px solid #005695;
  }

  >>> .filter_number .el-button {
    background: rgba(0, 86, 149, 0.7);
    color: #ffffff;
  }

  >>> .filter_number .el-button--primary:first-child {
    border-right: 0px solid rgba(0, 86, 149, 1);
    border-left: 1px solid rgba(0, 86, 149, 1);
    border-top: 1px solid rgba(0, 86, 149, 1);
    border-bottom: 1px solid rgba(0, 86, 149, 1);
  }

  >>> .filter_number .el-button--primary:last-child {
    border-right: 1px solid rgba(0, 86, 149, 1);
    border-left: 0px solid rgba(0, 86, 149, 1);
    border-top: 1px solid rgba(0, 86, 149, 1);
    border-bottom: 1px solid rgba(0, 86, 149, 1);
    margin-left: 1px;
  }

  >>> .filter_number .el-button--primary:last-child:hover {
    border-right: 1px solid #005695;
    border-top: 1px solid #005695;
    border-bottom: 1px solid #005695;
  }

  >>> .filter_number .el-button--primary:first-child:hover {
    border-left: 1px solid #005695;
    border-top: 1px solid #005695;
    border-bottom: 1px solid #005695;
  }

  >>> .filter_tag .el-button {
    background: rgba(87, 204, 244, 0.7);
    color: #ffffff;
  }

  >>> .filter_tag .el-button--primary:first-child {
    border-right: 0px solid rgba(87, 204, 244, 1);
    border-left: 1px solid rgba(87, 204, 244, 1);
    border-top: 1px solid rgba(87, 204, 244, 1);
    border-bottom: 1px solid rgba(87, 204, 244, 1);
  }

  >>> .filter_tag .el-button--primary:last-child {
    border-right: 1px solid rgba(87, 204, 244, 1);
    border-left: 0px solid rgba(87, 204, 244, 1);
    border-top: 1px solid rgba(87, 204, 244, 1);
    border-bottom: 1px solid rgba(87, 204, 244, 1);
    margin-left: 1px;
  }

  >>> .filter_tag .el-button--primary:last-child:hover {
    border-right: 1px solid #005695;
    border-top: 1px solid #005695;
    border-bottom: 1px solid #005695;
  }

  >>> .filter_tag .el-button--primary:first-child:hover {
    border-left: 1px solid #005695;
    border-top: 1px solid #005695;
    border-bottom: 1px solid #005695;
  }
</style>
